/**
 * appleAuth — Sign in with Apple using a MANUAL popup + fragment flow.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Why we don't use Apple's `AppleID.auth.signIn()` JS SDK
 * -------------------------------------------------------
 * The SDK's popup path is hard-wired to `response_mode=web_message`,
 * which relies on Apple's authorize page being able to
 * `window.opener.postMessage(...)` the response back to us. Under
 * Cross-Origin-Opener-Policy (COOP) that link is severed — Apple
 * silently fails to deliver the response and either:
 *   • shows "Sign Up Not Completed" inside the popup, or
 *   • closes the popup and leaves the opener's promise hanging
 *     forever on a blank white page.
 *
 * Instead we build the authorize URL ourselves with
 * `response_mode=fragment`, `window.open()` it, and let Apple redirect
 * the popup back to our registered `redirect_uri` (= the origin, no
 * path) with the response in the URL fragment. `index.js` detects that
 * fragment on boot and forwards the payload to this opener via three
 * parallel channels (localStorage `storage` event, BroadcastChannel,
 * `window.opener.postMessage`). Whichever channel arrives first wins.
 *
 * `scope` is INTENTIONALLY omitted. Requesting `name email` would force
 * Apple to switch back to `response_mode=form_post`, which is unusable
 * in an SPA. Xano decodes the JWT `identity_token` server-side, so the
 * frontend never needs the user object.
 *
 * signIn() resolves with:
 *   {
 *     code:            <string>,   // Apple authorization code (single-use)
 *     identityToken:   <string>,   // Apple id_token JWT — sent to Xano as identity_token
 *     state:           <string>,
 *     user:            null,       // Not returned when scope is omitted; Xano derives from JWT
 *   }
 */
import { useCallback, useRef, useState } from "react";
import {
  APPLE_RESULT_KEY,
  BROADCAST_CHANNEL_NAME,
  POPUP_MESSAGE_TYPE,
  parseAppleResponse,
} from "./applePopupBridge";

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
const APPLE_AUTHORIZE_URL = "https://appleid.apple.com/auth/authorize";
const POPUP_NAME = "brontoy-apple-signin";
const POPUP_FEATURES = "width=520,height=680,scrollbars=yes,resizable=yes";

function randomString(length = 32) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => (b % 36).toString(36))
    .join("")
    .slice(0, length);
}

/** Attach a short code + message to an Error so callers can branch on it. */
function makeAppleError(code, message) {
  const e = new Error(message);
  e.appleErrorCode = code;
  return e;
}

/**
 * useAppleSignIn — React hook.
 *
 * Returns `{ ready, error, signIn }` — same shape as the previous SDK
 * wrapper so `Landing.jsx` doesn't need to change.
 *
 * `ready` is always `true` after mount — there's no external SDK to
 * load. `error` reflects a startup misconfiguration (missing env var).
 */
export function useAppleSignIn() {
  const [ready] = useState(true);
  const [error] = useState(
    APPLE_CLIENT_ID ? null : new Error("REACT_APP_APPLE_CLIENT_ID is not set")
  );
  const inFlightRef = useRef(false);

  const signIn = useCallback(async () => {
    if (!APPLE_CLIENT_ID)
      throw makeAppleError(
        "sdk_not_ready",
        "Apple Sign-In is not configured. Please contact support."
      );
    if (inFlightRef.current)
      throw makeAppleError(
        "in_flight",
        "A sign-in attempt is already in progress."
      );
    inFlightRef.current = true;

    // Fresh state + nonce per attempt so Apple treats each try as a
    // brand-new session — reusing them causes the SDK's "sign in not
    // completed" retry error.
    const state = randomString(24);
    const nonce = randomString(32);

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    //
    // Apple's Services ID has the bare ORIGIN registered as the Return
    // URL. `redirect_uri` here MUST match that exactly — no path, no
    // trailing slash. Apple will redirect the popup back to this URL
    // with the auth response in the fragment; `index.js` catches it.
    const redirectURI = window.location.origin;

    const authorizeUrl = new URL(APPLE_AUTHORIZE_URL);
    authorizeUrl.searchParams.set("client_id", APPLE_CLIENT_ID);
    authorizeUrl.searchParams.set("redirect_uri", redirectURI);
    authorizeUrl.searchParams.set("response_type", "code id_token");
    // Fragment mode keeps the response client-visible (SPA can read
    // it) and avoids the SDK's flaky web_message handshake.
    authorizeUrl.searchParams.set("response_mode", "fragment");
    authorizeUrl.searchParams.set("state", state);
    authorizeUrl.searchParams.set("nonce", nonce);
    // NO `scope` — see file header for why.

    console.log("[BrontoY Apple] opening popup:", {
      redirectURI,
      response_mode: "fragment",
      state_preview: state.slice(0, 8),
    });

    const popup = window.open(
      authorizeUrl.toString(),
      POPUP_NAME,
      POPUP_FEATURES
    );

    if (!popup) {
      inFlightRef.current = false;
      throw makeAppleError(
        "popup_blocked",
        "Your browser blocked the Apple sign-in popup. Please allow popups for this site and try again."
      );
    }

    try {
      return await new Promise((resolve, reject) => {
        let done = false;
        let bc = null;
        let pollHandle = null;

        const cleanup = () => {
          window.removeEventListener("storage", onStorage);
          window.removeEventListener("message", onMessage);
          if (bc) {
            try {
              bc.close();
            } catch {
              /* ignore */
            }
          }
          if (pollHandle) clearInterval(pollHandle);
        };

        const finish = (payload) => {
          if (done) return;
          if (!payload || typeof payload !== "object") return;
          if (payload.type && payload.type !== POPUP_MESSAGE_TYPE) return;

          done = true;
          cleanup();

          // Close the popup if it's still around — we've already got
          // the payload we care about.
          try {
            if (popup && !popup.closed) popup.close();
          } catch {
            /* not owner — ignore */
          }

          if (payload.state && state && payload.state !== state) {
            reject(
              makeAppleError(
                "state_mismatch",
                "Apple sign-in session expired. Please try signing in again."
              )
            );
            return;
          }
          if (payload.error) {
            const raw = payload.error;
            if (/user_cancelled|user_canceled/i.test(raw)) {
              reject(makeAppleError("cancelled", "Sign in was cancelled."));
              return;
            }
            if (/invalid_client/i.test(raw)) {
              reject(
                makeAppleError(
                  "invalid_client",
                  "This site is not authorised in Apple Developer for Sign in with Apple. Please contact support."
                )
              );
              return;
            }
            reject(
              makeAppleError(
                "apple_error",
                payload.error_description || raw || "Apple Sign-In failed."
              )
            );
            return;
          }
          if (!payload.code) {
            reject(
              makeAppleError(
                "no_code",
                "Apple did not return an authorization code. Please try again."
              )
            );
            return;
          }
          if (!payload.id_token) {
            reject(
              makeAppleError(
                "no_identity_token",
                "Apple did not return an identity token. Please try again."
              )
            );
            return;
          }
          resolve({
            code: payload.code,
            identityToken: payload.id_token,
            state: payload.state,
            user: payload.user || null,
          });
        };

        // Channel 1: localStorage `storage` events — the most reliable
        // path under COOP.
        function onStorage(e) {
          if (e.key !== APPLE_RESULT_KEY || !e.newValue) return;
          try {
            finish(JSON.parse(e.newValue));
          } catch {
            /* ignore malformed */
          }
        }
        window.addEventListener("storage", onStorage);

        // Channel 2: BroadcastChannel — works when opener + popup
        // share a browsing-context group.
        try {
          bc = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
          bc.onmessage = (e) => finish(e.data);
        } catch {
          /* not supported — ignore */
        }

        // Channel 3: window.postMessage — only survives when COOP
        // didn't sever the link, but essentially free to keep.
        function onMessage(e) {
          if (e.origin !== window.location.origin) return;
          finish(e.data);
        }
        window.addEventListener("message", onMessage);

        // Also poll the popup every 400ms so if the user closes it
        // manually (or Apple closes it after an error the fragment
        // handler couldn't parse) we don't hang forever.
        pollHandle = setInterval(() => {
          if (done) return;
          if (popup.closed) {
            // Small grace window in case a storage event is still in
            // flight from the popup's `beforeunload`.
            setTimeout(() => {
              if (done) return;
              // Last-chance: read localStorage directly.
              try {
                const raw = localStorage.getItem(APPLE_RESULT_KEY);
                if (raw) {
                  const parsed = JSON.parse(raw);
                  localStorage.removeItem(APPLE_RESULT_KEY);
                  finish(parsed);
                  return;
                }
              } catch {
                /* ignore */
              }
              done = true;
              cleanup();
              reject(
                makeAppleError("cancelled", "Sign in with Apple was cancelled.")
              );
            }, 250);
          }
        }, 400);

        // Safety net — Apple sign-in should never take more than ~5
        // minutes. If it does, tear down.
        setTimeout(() => {
          if (done) return;
          done = true;
          cleanup();
          try {
            if (popup && !popup.closed) popup.close();
          } catch {
            /* ignore */
          }
          reject(
            makeAppleError(
              "timeout",
              "Sign in with Apple timed out. Please try again."
            )
          );
        }, 5 * 60 * 1000);
      });
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  return { ready, error, signIn };
}

// Re-export the parser so consumers (e.g. AppleCallback.jsx) can use
// the same shape without importing two modules.
export { parseAppleResponse };
